/**
 * Severity marks shared by React components and CodeMirror DOM: small knotted-yarn dots, a knot
 * tied on a strand of yarn. Shape, not colour alone, tells the severity apart:
 *   error = solid knot with a "!" knocked out · warning = open ring knot · info = small knot.
 */
import type { Severity } from '../engine/types';

/** The strand of yarn the knot is tied on, both ends (16 px grid). */
const TAIL = 'M.8 8c1.2-1 2.1-1 3.1 0M12.1 8c1 1 1.9 1 3.1 0';

export interface KnotShape {
  /** Solid disc radius, or null. */
  disc: number | null;
  /** Ring radius and stroke width, or null. */
  ring: [number, number] | null;
  /** Knock-out "!" inside the disc. */
  bang: boolean;
}

export const KNOT: Record<Severity, KnotShape> = {
  error: { disc: 5, ring: null, bang: true },
  warning: { disc: null, ring: [4, 1.9], bang: false },
  info: { disc: 2.9, ring: null, bang: false },
};

export const KNOT_CENTRE = 8;
export const KNOT_TAIL = TAIL;
/** The "!" knocked out of the error knot. */
export const KNOT_BANG = 'M8 5.6v2.9M8 10.4v.1';

/** Same drawing as <SeverityIcon>, as a string for CodeMirror gutter markers and tooltips. */
export function severityIconSvg(severity: Severity, size = 12): string {
  const k = KNOT[severity];
  const c = KNOT_CENTRE;
  const parts: string[] = [];
  if (k.disc) parts.push(`<circle cx="${c}" cy="${c}" r="${k.disc}" fill="currentColor"/>`);
  if (k.ring) parts.push(`<circle cx="${c}" cy="${c}" r="${k.ring[0]}" fill="none" stroke="currentColor" stroke-width="${k.ring[1]}"/>`);
  if (k.bang)
    parts.push(`<path d="${KNOT_BANG}" stroke="rgb(var(--c-editor))" stroke-width="1.5" stroke-linecap="round" fill="none"/>`);
  parts.push(`<path d="${TAIL}" fill="none" stroke="currentColor" stroke-width="1.3" stroke-linecap="round"/>`);
  return `<svg viewBox="0 0 16 16" width="${size}" height="${size}" aria-hidden="true" focusable="false">${parts.join('')}</svg>`;
}

/** Editor gutter markers use the same knots. */
export const yarnDotSvg = severityIconSvg;
